<script lang="ts">
	/* eslint-disable no-alert */
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import { maxFileSize } from "../../../config/maxFileSize.js";
	import { createEventDispatcher } from "svelte";
	import Button from "../Button.svelte";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import Icon from "../Icon.svelte";
	import imageUpload from "../../../../core/assets/icons/image-upload.svg?raw";
	import { getClients } from "../../appsync/getClients.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";

	export let destinationFolder: string;
	export let multiple = false;
	export let disabled = false;
	export let currentFiles: UploadedFile[] = [];
	export let accept: string | string[] = "*/*";
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	export let maximum: number = multiple ? Infinity : 1;
	// eslint-disable-next-line @typescript-eslint/prefer-as-const
	export let bucket: "upload" = "upload"; // Add more buckets if needed
	export let required = false;
	export let imagesInRow = false;

	const dispatch = createEventDispatcher();
	const globalSettings = getGlobalSettings();

	$: clientPromise = {
		upload: getClients(globalSettings).then(({ uploadStorage }) => uploadStorage),
	}[bucket];

	let alreadyUploaded: [File, UploadedFile][] = [];

	const upload = createPromiseStore(async (filesToUpload: File[]) => {
		const client = await clientPromise;
		return await Promise.all(
			Array.from(filesToUpload).map(async (file) =>
				client.uploadFile(destinationFolder, file).then((s3File) => {
					alreadyUploaded.push([file, s3File]);
					return s3File;
				}),
			),
		);
	});

	let input: HTMLInputElement | undefined = undefined;
	let fileList: FileList | undefined;

	function onChange(files: UploadedFile[]): void {
		dispatch("change", { files: multiple ? files : files[0] });
	}

	onChange(currentFiles);

	async function uploadFiles(fileList: FileList): Promise<void> {
		try {
			const filesToUpload = Array.from(fileList).filter(
				(file) => !alreadyUploaded.some(([f]) => `${f.name}.${f.type}` === `${file.name}.${f.type}`),
			);

			if (maximum && filesToUpload.length + currentFiles.length > maximum) {
				alert(`Maximální počet souborů je ${maximum}`);
				return;
			}

			for (const file of filesToUpload) {
				if (file.size > maxFileSize) {
					alert(
						`Soubor ${file.name} je příliš velký (${Math.round(file.size / 1024 / 1024)}). Maximální povolená velikost souboru je ${maxFileSize / 1024 / 1024} MB.`,
					);
					return;
				}

				const allowedFileTypes = Array.isArray(accept) ? accept : accept.split(",");
				if (accept !== "*/*" && !allowedFileTypes.some((type) => file.type.startsWith(type))) {
					alert(`Soubor '${file.name}' má nepodporovaný typ.`);
					return;
				}
			}

			const files = await upload.call(filesToUpload);
			if (multiple) {
				currentFiles = [...currentFiles, ...files];
			} else {
				// eslint-disable-next-line require-atomic-updates
				currentFiles = files;
			}
			onChange(currentFiles);
		} finally {
			if (input) {
				input.value = "";
			}
		}
	}

	$: {
		if (fileList) {
			void uploadFiles(fileList);
		}
	}

	function onDelete(file: UploadedFile): void {
		if (input) {
			input.value = "";
		}
		currentFiles = currentFiles.filter((search) => search.path !== file.path);
		onChange(currentFiles);
	}

	$: {
		alreadyUploaded = alreadyUploaded.filter(([_, s3File]) => currentFiles.includes(s3File));
	}

	function onInputChange({ target }: Event): void {
		const input = target as HTMLInputElement;
		if (input.files) {
			void uploadFiles(input.files);
		}
	}

	$: isDisabled = disabled || $upload.status === "loading";
	let loadingInput: HTMLInputElement | null = null;
	$: {
		if (loadingInput) {
			loadingInput.setCustomValidity("Vyčkejte prosím na nahrání souboru");
		}
	}

	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<div class="relative flex flex-col gap-4">
	{#if $upload.status === "loading"}
		<input bind:this={loadingInput} type="text" required class="absolute opacity-0" tabindex="-1" />
	{/if}
	{#if currentFiles.length < maximum}
		<div
			class="bg-secondary-150 border-secondary-250 relative flex h-32 w-full items-center justify-center rounded-lg border-2 border-dashed p-4"
		>
			<input
				aria-label="Nahrát soubor"
				bind:this={input}
				type="file"
				class="absolute inset-0 h-full w-full opacity-0"
				class:cursor-pointer={!isDisabled}
				class:cursor-not-allowed={isDisabled}
				accept={Array.isArray(accept) ? accept.join(",") : accept}
				disabled={isDisabled}
				{multiple}
				on:change={onInputChange}
				{required}
			/>
			<div class="pointer-events-none flex h-full w-full items-center justify-center">
				{#if $upload.status === "loading"}
					<span class="text-2xs">Nahrávání</span>
				{:else}
					<span class="flex items-center gap-2">
						<Icon class="h-[2.875rem]" icon={imageUpload} />
						<span class="text-secondary-300 text-2xs leading-[1.2]">
							Sem přetáhněte
							{#if multiple}
								soubory
							{:else}
								soubor
							{/if}
							nebo
							<span class="block underline">vyberte z úložiště</span>
						</span>
					</span>
				{/if}
			</div>
		</div>
	{/if}
	{#if currentFiles.length > 0}
		<div class="flex gap-2 {imagesInRow ? 'flex-row flex-wrap' : 'flex-col'}">
			{#each currentFiles as file (file.path)}
				<div class="flex items-center gap-2">
					{#if file.type.startsWith("image/")}
						<DynamicPicture
							image={file}
							imgClass="aspect-square w-24 object-contain"
							alt={file.title ?? "Náhled nahraného souboru"}
							lightboxId={file.path}
							lightboxWidth={600}
							height={96}
							width={96}
							{bucket}
						/>
					{/if}
					<span class="max-w-48 overflow-clip text-ellipsis">{file.title}</span>
					<Button
						variant="secondary"
						on:click={() => {
							onDelete(file);
						}}
					>
						×
					</Button>
				</div>
			{/each}
		</div>
	{/if}
</div>
